import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./index.css";
import "../../site/site.css";
import CustomLoader from "../../components/CustomLoader/index";
import MetaTag from "../../components/MetaTag/MetaTag";
import PropertyImage from "../../components/Property/PropertyImage";
import SiteHeader from "../../site/SiteHeader";
import SiteFooter from "../../site/SiteFooter";
import { Eyebrow, Reveal } from "../../site/Brand";
import { localized, publicApi } from "../../api/client";
import { POST_CATEGORIES, categoryLabel } from "../../utils/postCategories";

const FIRST_PAGE = 9;
const NEXT_PAGE = 6;

export const formatPostDate = (date, lang) =>
  date
    ? new Date(date).toLocaleDateString(lang === "en" ? "en-US" : "ru-RU", { year: "numeric", month: "long", day: "numeric" })
    : "";

const News = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [category, setCategory] = useState("");

  const load = (offset, cat = category) => {
    setLoading(true);
    setError(false);
    publicApi
      .posts({ limit: offset ? NEXT_PAGE : FIRST_PAGE, offset, category: cat })
      .then((data) => {
        setPosts((prev) => (offset ? [...prev, ...data.items] : data.items));
        setHasMore(data.hasMore);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setPosts([]);
    load(0, category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const excerpt = (post) => {
    const e = localized(lang, post.excerpt_ru, post.excerpt_en);
    if (e) return e;
    const c = localized(lang, post.content_ru, post.content_en);
    return c.length > 160 ? `${c.slice(0, 160)}…` : c;
  };

  return (
    <>
      <MetaTag title={t("seo.news.title")} description={t("seo.news.description")} />
      <SiteHeader />
      <main>
        <section className="page-head">
          <div className="wrap">
            <Reveal>
              <Eyebrow line>{t("site.nav.news")}</Eyebrow>
              <h1 className="display">{t("news.title")}</h1>
              <p className="lead">{t("news.description")}</p>
            </Reveal>
          </div>
        </section>

        <section className="news">
          <div className="wrap">
            <div className="news-tabs">
              {[{ key: "", label: "news.all" }, ...POST_CATEGORIES].map((c) => (
                <button key={c.key || "all"} type="button" className={category === c.key ? "is-active" : ""} onClick={() => setCategory(c.key)}>
                  {t(c.label)}
                </button>
              ))}
            </div>

            {posts.length > 0 && (
              <div className="news-grid">
                {posts.map((post) => {
                  const title = localized(lang, post.title_ru, post.title_en);
                  return (
                    <Link to={`/news/${post.id}`} key={post.id} className="news-card">
                      <div className="news-card__media">
                        <PropertyImage src={post.image_url} alt={title} />
                        {post.category && <span className="chip chip--light">{categoryLabel(t, post.category)}</span>}
                      </div>
                      <span className="news-card__date">{formatPostDate(post.created_at, lang)}</span>
                      <h2 className="news-card__title">{title}</h2>
                      <p className="news-card__excerpt">{excerpt(post)}</p>
                    </Link>
                  );
                })}
              </div>
            )}

            {!loading && !error && posts.length === 0 && (
              <div className="catalog-empty">
                <p className="lead">{t("news.noPosts")}</p>
              </div>
            )}
            {error && (
              <div className="catalog-empty">
                <p className="lead">{t("common.loadError")}</p>
                <button type="button" className="btn-pill" onClick={() => load(posts.length)}>
                  {t("common.retry")}
                </button>
              </div>
            )}
            {loading && <CustomLoader loading />}
            {!loading && hasMore && (
              <div className="catalog-more">
                <button type="button" className="btn-pill btn-pill--ghost" onClick={() => load(posts.length)}>
                  {t("news.loadMore")}
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
};

export default News;
